// src/utils/security.js
// Camada centralizada de segurança: sanitização de dados, validação de entrada
// e proteção contra força bruta no login.

// ─── Sanitização de strings ───────────────────────────────────────────────────

const MAX_STRING_LENGTH = 2000;

// Padrões que indicam tentativas de injeção ou XSS
const DANGEROUS_PATTERNS = [
  /<script[\s\S]*?>/gi,
  /<\/script>/gi,
  /javascript\s*:/gi,
  /vbscript\s*:/gi,
  /on\w+\s*=\s*["'`]?/gi,   // event handlers: onclick=, onload=, etc.
  /data\s*:\s*text\/html/gi,
  /<\s*iframe/gi,
  /<\s*object/gi,
  /<\s*embed/gi,
  /<\s*form/gi,
  /\$\s*\{/g,                // template literal injection: ${...}
  /\.\s*__proto__/gi,        // prototype pollution
  /\.\s*constructor/gi,
  /\.\s*prototype/gi,
];

/**
 * Sanitiza uma string removendo tags HTML e padrões de injeção.
 * Seguro para usar antes de salvar no Firestore ou exibir sem escape.
 *
 * EXCEÇÃO: data URLs de imagem (base64) são preservadas integralmente —
 * não contêm HTML injetável e seriam corrompidas pelo truncamento de 2000 chars.
 */
export function sanitizeString(value) {
  if (typeof value !== 'string') return value;
  // Preserva base64 de imagens/arquivos sem truncar nem sanitizar padrões HTML
  if (/^data:(image|application)\/[a-zA-Z0-9.+-]+;base64,/.test(value)) return value;
  let s = value.trim().slice(0, MAX_STRING_LENGTH);
  // Remove padrões perigosos
  DANGEROUS_PATTERNS.forEach((re) => { s = s.replace(re, ''); });
  // Remove qualquer tag HTML restante
  s = s.replace(/<[^>]*>/g, '');
  return s;
}

/**
 * Sanitiza recursivamente um objeto antes de enviar ao Firestore.
 * Preserva tipos nativos (number, boolean, Date, null).
 */
export function sanitizeData(data) {
  if (data === null || data === undefined) return data;
  if (typeof data === 'string')  return sanitizeString(data);
  if (typeof data === 'number')  return Number.isFinite(data) ? data : 0;
  if (typeof data === 'boolean') return data;
  if (data instanceof Date)      return data;
  // Firestore Timestamp (tem .toDate) — preservar
  if (typeof data === 'object' && typeof data.toDate === 'function') return data;
  if (Array.isArray(data)) return data.map(sanitizeData);
  if (typeof data === 'object') {
    // Objetos especiais do Firebase SDK (FieldValue: serverTimestamp, arrayUnion, increment, etc.)
    // não são plain objects — têm prototype diferente de Object e não devem ser processados.
    if (Object.getPrototypeOf(data) !== Object.prototype) return data;
    return Object.fromEntries(
      Object.entries(data).map(([k, v]) => [k, sanitizeData(v)])
    );
  }
  return data;
}

// ─── Validação de campos comuns ───────────────────────────────────────────────

/** Valida formato de e-mail */
export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((email || '').trim());
}

/** Valida telefone BR: 10 ou 11 dígitos */
export function isValidPhone(phone) {
  const digits = (phone || '').replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 11;
}

/** Valida nome: ao menos 2 caracteres, sem caracteres especiais estranhos */
export function isValidName(name) {
  const clean = (name || '').trim();
  return clean.length >= 2 && clean.length <= 200;
}

/** Remove caracteres não numéricos de um telefone/CPF */
export function digitsOnly(value) {
  return (value || '').replace(/\D/g, '');
}

/** Valida CPF brasileiro com algoritmo de dígitos verificadores */
export function isValidCPF(cpf) {
  const d = (cpf || '').replace(/\D/g, '');
  if (d.length !== 11) return false;
  if (/^(.)\1{10}$/.test(d)) return false; // todos dígitos iguais (ex: 111.111.111-11)
  const calc = (slice, weights) => {
    const sum = slice.split('').reduce((acc, n, i) => acc + Number(n) * weights[i], 0);
    const rem = sum % 11;
    return rem < 2 ? 0 : 11 - rem;
  };
  const d1 = calc(d.slice(0, 9), [10,9,8,7,6,5,4,3,2]);
  if (d1 !== Number(d[9])) return false;
  const d2 = calc(d.slice(0, 10), [11,10,9,8,7,6,5,4,3,2]);
  return d2 === Number(d[10]);
}

/** Formata CPF como XXX.XXX.XXX-XX enquanto o usuário digita */
export function formatCPF(cpf) {
  const d = (cpf || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`;
  return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
}

/** Valida CNPJ brasileiro com algoritmo de dígitos verificadores */
export function isValidCNPJ(cnpj) {
  const d = (cnpj || '').replace(/\D/g, '');
  if (d.length !== 14) return false;
  if (/^(.)\1{13}$/.test(d)) return false;
  const calc = (slice, weights) => {
    const sum = slice.split('').reduce((acc, n, i) => acc + Number(n) * weights[i], 0);
    const rem = sum % 11;
    return rem < 2 ? 0 : 11 - rem;
  };
  const d1 = calc(d.slice(0, 12), [5,4,3,2,9,8,7,6,5,4,3,2]);
  if (d1 !== Number(d[12])) return false;
  const d2 = calc(d.slice(0, 13), [6,5,4,3,2,9,8,7,6,5,4,3,2]);
  return d2 === Number(d[13]);
}

/** Formata CNPJ como XX.XXX.XXX/XXXX-XX enquanto o usuário digita */
export function formatCNPJ(cnpj) {
  const d = (cnpj || '').replace(/\D/g, '').slice(0, 14);
  if (d.length <= 2)  return d;
  if (d.length <= 5)  return `${d.slice(0,2)}.${d.slice(2)}`;
  if (d.length <= 8)  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`;
}

// ─── Proteção contra brute force (rate limiting no login) ────────────────────

const LOCK_KEY     = '__sec_lock__';
const ATTEMPTS_KEY = '__sec_att__';
const MAX_ATTEMPTS = 4;           // tentativas antes de bloquear
const LOCK_MS      = 15 * 60 * 1000; // 15 minutos de bloqueio

/** Registra uma tentativa de login com falha. Retorna o total de tentativas. */
export function recordFailedAttempt() {
  const prev = Number(sessionStorage.getItem(ATTEMPTS_KEY) || 0);
  const next = prev + 1;
  sessionStorage.setItem(ATTEMPTS_KEY, String(next));
  if (next >= MAX_ATTEMPTS) {
    sessionStorage.setItem(LOCK_KEY, String(Date.now() + LOCK_MS));
  }
  return next;
}

/** Limpa o contador e o bloqueio (chamar após login com sucesso). */
export function clearLoginAttempts() {
  sessionStorage.removeItem(LOCK_KEY);
  sessionStorage.removeItem(ATTEMPTS_KEY);
}

/**
 * Retorna os segundos restantes de bloqueio.
 * Se retornar 0, o usuário pode tentar novamente.
 */
export function getLockRemainingSeconds() {
  const until = Number(sessionStorage.getItem(LOCK_KEY) || 0);
  if (!until) return 0;
  const remaining = until - Date.now();
  if (remaining <= 0) {
    clearLoginAttempts();
    return 0;
  }
  return Math.ceil(remaining / 1000);
}

/** Quantas tentativas ainda restam antes do bloqueio. */
export function getRemainingAttempts() {
  const count = Number(sessionStorage.getItem(ATTEMPTS_KEY) || 0);
  return Math.max(0, MAX_ATTEMPTS - count);
}

// ─── Segurança de tokens / headers ───────────────────────────────────────────

/**
 * Verifica se o domínio atual é confiável (produção ou localhost).
 * Use para bloquear execução em domínios suspeitos / clonados.
 */
export function isTrustedDomain() {
  const { hostname } = window.location;
  const trusted = [
    'localhost',
    '127.0.0.1',
    'sistemaagenda-78dc2.firebaseapp.com',
    'sistemaagenda-78dc2.web.app',
    'agendaprogg.com.br',
    'www.agendaprogg.com.br',
  ];
  return trusted.some((d) => hostname === d || hostname.endsWith('.' + d));
}
